import React, { useCallback, useEffect, useState } from "react";
import styled from "@emotion/styled/macro";
import {
  Banner,
  BannerTypes,
  Modal,
  Switch,
  Typography,
} from "@sunrun/experience-ui-components";
import {
  getBrowserGeolocation,
  isEligibleForRemoteWork,
} from "checkout/util/geoUtils";

const REMOTE_WORK_DESIGN_PREFS_GATE_MESSAGE =
  "This feature is not available until design preferences are filled out. It will be automatically enabled when that step is complete.";

interface RemoteWorkSettingsModalProps {
  onClose: () => void;
  onSave: (isRemoteWorking: boolean) => void;
  customerLatitude?: number;
  customerLongitude?: number;
  initialIsRemoteWorking?: boolean;
  isDesignPreferencesComplete: boolean;
}

const RemoteWorkSettingsModal: React.FC<RemoteWorkSettingsModalProps> = ({
  onClose,
  onSave,
  customerLatitude,
  customerLongitude,
  initialIsRemoteWorking,
  isDesignPreferencesComplete,
}) => {
  const [isRemoteWorking, setIsRemoteWorking] = useState(
    initialIsRemoteWorking ?? false
  );
  const [isEligible, setIsEligible] = useState<boolean | null>(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [locationDenied, setLocationDenied] = useState(false);

  useEffect(() => {
    if (!isDesignPreferencesComplete) {
      return;
    }

    const checkDistance = async () => {
      setIsLoadingLocation(true);

      if (customerLatitude == null || customerLongitude == null) {
        setIsEligible(false);
        setLocationDenied(true);
        setIsLoadingLocation(false);
        return;
      }

      try {
        const position = await getBrowserGeolocation();
        setIsEligible(
          isEligibleForRemoteWork(
            position.coords.latitude,
            position.coords.longitude,
            customerLatitude,
            customerLongitude
          )
        );
      } catch (error) {
        console.error("Error getting browser geolocation:", error);
        setIsEligible(false);
        setLocationDenied(true);
      } finally {
        setIsLoadingLocation(false);
      }
    };

    checkDistance();
  }, [customerLatitude, customerLongitude, isDesignPreferencesComplete]);

  const handleSave = useCallback(() => {
    onSave(isRemoteWorking);
  }, [isRemoteWorking, onSave]);

  const showWarning = !isLoadingLocation && !isEligible;
  const toggleDisabled =
    !isDesignPreferencesComplete || isLoadingLocation || !isEligible;

  return (
    <Modal
      testId="remote-work-settings-modal"
      title="Settings"
      hideIcon
      onClose={onClose}
      primaryButton={{
        isLoading: isLoadingLocation,
        testId: "remote-work-settings-save",
        text: "Save",
        onClick: handleSave,
        disabled: toggleDisabled,
      }}
      secondaryButton={{
        testId: "remote-work-settings-cancel",
        text: "Cancel",
        onClick: onClose,
      }}
    >
      <ModalContent>
        <Typography>
          Additional Home Review options are only available if you are working
          remotely.
        </Typography>

        {!isDesignPreferencesComplete ? (
          <Banner bannerType={BannerTypes.WarningLight} show showClose={false}>
            {REMOTE_WORK_DESIGN_PREFS_GATE_MESSAGE}
          </Banner>
        ) : (
          showWarning && (
            <Banner
              bannerType={BannerTypes.WarningLight}
              show
              showClose={false}
            >
              {locationDenied
                ? "We are unable to determine your location. Please check your browser and device settings to allow location sharing."
                : "This feature is not available at your current location. It will be automatically enabled when you are further away from the customer’s home."}
            </Banner>
          )
        )}

        <ToggleRow>
          <Switch
            disabled={toggleDisabled}
            size="large"
            checked={isRemoteWorking}
            onChange={(checked) => {
              setIsRemoteWorking(checked);
            }}
            onClick={(e) => {
              if (toggleDisabled) e.preventDefault();
            }}
            testId="remote-work-toggle"
          />
          <Typography fontWeight="medium">I'm Working remotely</Typography>
        </ToggleRow>
      </ModalContent>
    </Modal>
  );
};

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export { RemoteWorkSettingsModal, REMOTE_WORK_DESIGN_PREFS_GATE_MESSAGE };
